import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  makeStyles,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import Title from "./components/title";
import BoxBorder from "./components/box-border";
import DeviceStates from "./components/device/states/states";
import ExceptionsInfo from "./components/device/exceptions/exceptions";
import DeviceInfo from "./components/device/info/info";
import LogsModal from "./components/logs-modal";
import { getAxiosInstance } from "../../redux/common";

// import { data } from "./sample-data";
import Loader from "../../components/loader";
import { usePagination } from "../../hooks/usePagination/usePagination";
import { PAGE_CHANGE } from "../../hooks/usePagination/constants";
import { faVestPatches } from "@fortawesome/free-solid-svg-icons";

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    height: "100%",
    overflow: "auto",
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
    },
  },
  iconButtonPadding: {
    padding: 0,
  },
}));

const TestStandMonitoring = () => {
  const classes = useStyles();

  const [openLogsModal, setOpenLogsModal] = useState(false);

  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);

  const [exceptions, setExceptions] = useState([]);
  const [exceptionsItems, setExceptionsItems] = useState(0);
  const [exceptionsFetching, setExceptionsFetching] = useState(false);

  const { id, label } = useParams();

  const { paginationState, dispatch: dispatchPagination } = usePagination();
  const { page } = paginationState;

  useEffect(() => {
    if (id && page < 2) {
      loadLogs(page);
    } else {
      if (id) loadPaginatedExceptions();
    }
  }, [page, id]);

  const loadPaginatedExceptions = async () => {
    try {
      setExceptionsFetching(true);

      const response = await getAxiosInstance().get(
        "/screening/v2/hearbox/monitoring/owner/history/paginated",
        {
          params: {
            ...(id && {
              hearBoxId: id,
              page: page - 1,
              pageSize: 500,
              withDetails: true,
              eventType: "AUDIOMETER",
            }),
          },
        },
      );

      setExceptionsItems(response.data.numFound);

      const fetchedExceptions = response.data.entries
        .filter(
          (d) =>
            d.eventType === "AUDIOMETER" &&
            d.details.message &&
            JSON.parse(d.details.message).exceptions.length,
        )
        .map((d) => JSON.parse(d.details.message).exceptions)
        .flat();

      setExceptions((prevState) =>
        [...prevState, ...fetchedExceptions].sort(
          (a, b) => new Date(b.exceptionTs) - new Date(a.exceptionTs),
        ),
      );

      if (
        fetchedExceptions.length > 9 ||
        !exceptionsItems ||
        Math.ceil(exceptionsItems / 500) === 1 ||
        page === Math.ceil(exceptionsItems / 500)
      ) {
        setExceptionsFetching(false);
      }

      if (
        (!fetchedExceptions.length || fetchedExceptions.length < 10) &&
        page < Math.ceil(exceptionsItems / 500)
      ) {
        dispatchPagination({ type: PAGE_CHANGE, payload: page + 1 });
      }
    } catch (e) {
      setExceptionsFetching(false);
    }
  };

  const loadLogs = async (page, newest = true) => {
    try {
      // setFetching(true);
      setExceptionsFetching(true);
      dispatchPagination({ type: PAGE_CHANGE, payload: 1 });

      const response = await getAxiosInstance().get(
        "/screening/v2/hearbox/monitoring/owner/history/paginated",
        {
          params: {
            ...(id && {
              hearBoxId: id,
              page: 0,
              pageSize: 500,
              withDetails: true,
              eventType: "AUDIOMETER",
            }),
          },
        },
      );

      setExceptionsItems(response.data.numFound);

      const fetchedExceptions = response.data.entries
        .filter(
          (d) =>
            d.eventType === "AUDIOMETER" &&
            d.details.message &&
            JSON.parse(d.details.message).exceptions.length,
        )
        .map((d) => JSON.parse(d.details.message).exceptions)
        .flat();

      setExceptions((prevState) =>
        [...fetchedExceptions].sort(
          (a, b) => new Date(b.exceptionTs) - new Date(a.exceptionTs),
        ),
      );

      if (fetchedExceptions.length > 9) {
        setExceptionsFetching(false);
      }

      if (!fetchedExceptions.length || fetchedExceptions.length < 10) {
        dispatchPagination({ type: PAGE_CHANGE, payload: page + 1 });
      }

      if (!newest) return;

      const responseGeneralData = await getAxiosInstance().get(
        "/screening/v2/hearbox/monitoring/owner/history/paginated",
        {
          params: {
            ...(id && {
              hearBoxId: id,
              page: 0,
              pageSize: 1,
              withDetails: true,
              eventType: "AUDIOMETER",
            }),
          },
        },
      );

      const diskResponse = await getAxiosInstance().get(
        "/screening/v2/hearbox/monitoring/owner/history/paginated",
        {
          params: {
            ...(id && {
              hearBoxId: id,
              page: 0,
              pageSize: 100,
              withDetails: true,
              eventType: "HDD",
            }),
          },
        },
      );
      const OSResponse = await getAxiosInstance().get(
        "/screening/v2/hearbox/monitoring/owner/history/paginated",
        {
          params: {
            ...(id && {
              hearBoxId: id,
              page: 0,
              pageSize: 100,
              withDetails: true,
              eventType: "OS_VERSION",
            }),
          },
        },
      );
      const serviceResponse = await getAxiosInstance().get(
        "/screening/v2/hearbox/monitoring/owner/history/paginated",
        {
          params: {
            ...(id && {
              hearBoxId: id,
              page: 0,
              pageSize: 100,
              withDetails: true,
              eventType: "SERVICE_STATE",
            }),
          },
        },
      );

      const hearboxResponse =
        serviceResponse.data.entries.find(
          (service) =>
            service.message === "hearbox" || service.message === "Hearbox",
        ) || "";
      const hearboxRunning =
        hearboxResponse.details.message.includes("Running") || false;

      const nginxResponse = serviceResponse.data.entries.find(
        (service) =>
          service.message === "nginx" || service.message === "HearboxUI",
      );
      const nginxRunning =
        nginxResponse.details.message.includes("Running") || false;

      const diskSpace =
        diskResponse.data.entries.find((d) => d.message.includes("Disk C"))
          .message || "";
      const osVersionEntry = OSResponse.data.entries[0] || "";
      const os = JSON.parse(osVersionEntry?.details.message).ostype || "---";

      const data = responseGeneralData.data.entries[0];

      const connectionWithServer = !(
        data.message === "ERROR - Unable to connect to the remote server" ||
        data.message.includes(
          "The remote server returned an error: (404) Not Found.",
        )
      );

      const timeOfflineExceeded =
        Math.floor((new Date() - new Date(data.headerTs)) / 60000) > 15;

      const parsedData = timeOfflineExceeded
        ? {
            status: "OFFLINE",
            ts: data.headerTs,
            exceptions: [],
            devicesStates: {},
          }
        : !connectionWithServer
        ? {
            status: "NOK",
            ts: data.headerTs,
            exceptions: [],
            devicesStates: {},
          }
        : JSON.parse(data.details.message);

      setData({
        ...parsedData,
        devicesStates: {
          ...parsedData.devicesStates,
          diskSpaceUsed: parseFloat(diskSpace.replace(/[^0-9.]/g, "")),
          os,
          frontendStatus: !timeOfflineExceeded ? nginxRunning : "NOT_KNOWN",
          backendStatus: !timeOfflineExceeded ? hearboxRunning : "NOT_KNOWN",
        },
      });
    } catch (e) {
      setExceptionsFetching(false);
    } finally {
      setFetching(false);
    }
  };

  const handleOpenLogsModal = () => setOpenLogsModal(true);

  if (fetching) return <Loader loading={true} text={"Pobieranie danych..."} />;

  const { ts, devicesStates, status } = data;

  return (
    <Box className={classes.rootContainer}>
      <Title
        title={`  Monitoring stanowiska: ${label}`}
        date={ts}
        status={status}
      />
      <BoxBorder>
        <Box position={"absolute"} right={"10px"} top={"5px"}>
          <IconButton
            disabled={exceptionsFetching}
            aria-label="Odśwież"
            onClick={() => {
              setExceptions([]);
              page !== 1
                ? dispatchPagination({ type: PAGE_CHANGE, payload: 1 })
                : loadLogs();
            }}
            className={classes.iconButtonPadding}
          >
            {!exceptionsFetching ? (
              <Tooltip title={exceptionsFetching ? "" : "Odśwież dane"}>
                <RefreshIcon />
              </Tooltip>
            ) : (
              <CircularProgress style={{ width: "20px", height: "20px" }} />
            )}
          </IconButton>
        </Box>
        <DeviceStates
          data={devicesStates}
          handleOpenModal={handleOpenLogsModal}
        />
      </BoxBorder>
      <br />
      <Title title={`Historia błędów`} />
      <BoxBorder>
        <ExceptionsInfo
          data={exceptions}
          dataLength={exceptionsItems}
          page={page}
          loading={exceptionsFetching}
          dispatchPagination={dispatchPagination}
        />
      </BoxBorder>
      <br />
      <Title title={"Ogólne informacje"} />
      <BoxBorder>
        <DeviceInfo data={devicesStates} />
      </BoxBorder>
      {openLogsModal && (
        <LogsModal open={openLogsModal} setOpen={setOpenLogsModal} />
      )}
    </Box>
  );
};

export default TestStandMonitoring;
